import { LangSwitchPill } from "@/components/LangSwitchPill";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { PasswordResetDialog } from "@/components/auth/PasswordResetDialog";


const PARTNER_ONBOARDING_PATH = "/partner/onboarding";
const PARTNER_ONBOARDING_URL = `https://partner.whatfinland.com${PARTNER_ONBOARDING_PATH}?next=${PARTNER_ONBOARDING_PATH}`;

function partnerOnboardingSearch() {
  return { next: PARTNER_ONBOARDING_PATH } as never;
}

export const Route = createFileRoute("/partner/login")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) => ({
    next: PARTNER_ONBOARDING_PATH,
    code: typeof search.code === "string" ? search.code : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Partner Login — What Finland" },
      { name: "description", content: "Log in to your What Finland partner portal." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerLoginPage,
});

type Mode = "password";

function PartnerLoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function finishAuthRedirect() {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const hasTokens = hash.has("access_token") || hash.has("refresh_token");
      const hasCode = Boolean(search.code) || new URLSearchParams(window.location.search).has("code");
      if (!hasTokens && !hasCode) return;


      setBusy(true);
      setErr(null);
      try {
        if (hasCode) {
          const code = search.code ?? new URLSearchParams(window.location.search).get("code");
          if (code) {
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) throw error;
          }
        }
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!data.session) throw new Error("Sign-in link expired. Please request a new one.");
        if (!cancelled) navigate({ to: PARTNER_ONBOARDING_PATH as never, search: partnerOnboardingSearch(), replace: true });
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Couldn't finish sign-in. Please request a new link.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    finishAuthRedirect();
    return () => { cancelled = true; };
  }, [navigate, search.code, search.next]);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      navigate({ to: PARTNER_ONBOARDING_PATH as never, search: partnerOnboardingSearch() });
    } catch (e: any) {
      setErr(e?.message || "Sign in failed. Check your email and password.");
    } finally {
      setBusy(false);
    }
  }

  async function signInWithGoogle() {
    setErr(null);
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/partner/login?next=${encodeURIComponent(PARTNER_ONBOARDING_PATH)}`,
        extraParams: { prompt: "select_account" },
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      navigate({ to: PARTNER_ONBOARDING_PATH as never, search: partnerOnboardingSearch() });
    } catch (e: any) {
      setErr(e?.message || "Google sign-in failed.");
      setBusy(false);
    }
  }



  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-8">
        <div className="flex items-center gap-2 text-slate-900 mb-2">
          <Building2 className="h-5 w-5" />
          <span className="text-xs uppercase tracking-widest text-slate-500">Partner Portal</span>
        </div>
        <h1 className="text-2xl font-semibold text-slate-900">Sign In</h1>
        <LangSwitchPill className="mt-3" />
        <p className="mt-2 text-sm text-slate-600">Sign in with Google or your email and password.</p>

        <div className="mt-6" />

        <form onSubmit={submitPassword} className="space-y-4">
          <Field label="Email" type="email" value={email} onChange={setEmail} />
          <Field label="Password" type="password" value={password} onChange={setPassword} />
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-2xl bg-slate-900 text-white font-medium px-5 py-3 hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
        </form>

        <button
          type="button"
          onClick={() => setResetOpen(true)}
          className="mt-4 w-full text-xs text-slate-500 hover:text-slate-900 underline"
        >
          Forgot password?
        </button>
        <PasswordResetDialog open={resetOpen} onOpenChange={setResetOpen} defaultEmail={email} />




        <p className="mt-6 text-xs text-slate-500 text-center">
          New partner?{" "}
          <Link to={"/partner/register" as never} className="underline font-medium text-slate-700">Get Started</Link>
          {" · "}
          <Link to={"/" as never} className="underline">Back To Whatfinland.com</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
      />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16.2 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.1 4 9.3 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.3 35.4 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.2 39.6 16 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.9 35.6 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}
